import { authFetch } from '@flexibilitypay/shared';

export type ComplianceErrorCode =
  | 'COMPLIANCE_FORBIDDEN'
  | 'ACCOUNT_NOT_FOUND'
  | 'COMPLIANCE_ALREADY_IN_STATE'
  | 'COMPLIANCE_INVALID_TRANSITION'
  | 'COMPLIANCE_UPSTREAM_ERROR'
  | 'AUTH_INVALID_TOKEN';

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T060
 * @see functional/Specs/specs_cycle-2.md HU-11-FE
 */
export class ComplianceApiError extends Error {
  constructor(
    public readonly code: ComplianceErrorCode | 'UNKNOWN',
    public readonly httpStatus: number,
  ) {
    super(code);
    this.name = 'ComplianceApiError';
  }
}

interface ErrorBodyShape {
  code?: string;
  message?: string;
  error?: { code?: string; message?: string };
}

function extractErrorCode(body: ErrorBodyShape | null): string | undefined {
  if (!body) return undefined;
  if (typeof body.code === 'string') return body.code;
  if (body.error && typeof body.error.code === 'string') return body.error.code;
  return undefined;
}

async function safeJson(response: Response): Promise<ErrorBodyShape | null> {
  try {
    return (await response.json()) as ErrorBodyShape;
  } catch {
    return null;
  }
}

function isComplianceCode(code: string | undefined): code is ComplianceErrorCode {
  return (
    code === 'COMPLIANCE_FORBIDDEN' ||
    code === 'ACCOUNT_NOT_FOUND' ||
    code === 'COMPLIANCE_ALREADY_IN_STATE' ||
    code === 'COMPLIANCE_INVALID_TRANSITION' ||
    code === 'COMPLIANCE_UPSTREAM_ERROR' ||
    code === 'AUTH_INVALID_TOKEN'
  );
}

async function performComplianceAction(
  accountId: string,
  action: 'block' | 'unblock',
  reason: string,
  idempotencyKey?: string,
): Promise<void> {
  const trimmedReason = reason.trim();
  const response = await authFetch(
    `/accounts/${encodeURIComponent(accountId)}/${action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: trimmedReason }),
      idempotencyKey,
    },
  );
  if (response.ok) return;
  const body = await safeJson(response);
  const code = extractErrorCode(body);
  throw new ComplianceApiError(
    isComplianceCode(code) ? code : 'UNKNOWN',
    response.status,
  );
}

export function blockAccount(
  accountId: string,
  reason: string,
  opts: { idempotencyKey?: string } = {},
): Promise<void> {
  return performComplianceAction(accountId, 'block', reason, opts.idempotencyKey);
}

export function unblockAccount(
  accountId: string,
  reason: string,
  opts: { idempotencyKey?: string } = {},
): Promise<void> {
  return performComplianceAction(accountId, 'unblock', reason, opts.idempotencyKey);
}
